import React, { useEffect, useRef } from 'react';
import { FaXmark } from 'react-icons/fa6';
import MobileMenuItem from './MobileMenuItem';
import EditModeToggle from './EditModeToggle';
import UserProfileButton from './UserProfileButton';

const MobileMenu = ({
    isOpen, // Controls Whether the mobile menu is currently open or closed.
    onClose, // Function to call when the menu should be closed.
    navbarMenus, // Array of menu items to display in the mobile menu.
    user, // Current User Object (null if not logged in).
    setSelected, // Function to set the currently selected menu item.
    onEnter, // Function called when a menu item is hovered over.
    onLeave,// Function called when a menu item is no longer hovered over.
    menusLoaded = false,
}) => {

    // Ref for the mobile menu container, used to manage focus and scroll behavior.
    const mobileMenuRef = useRef(null);

    // Close on escape key
    useEffect(() => {
        // Handler for Escape key to close the menu
        const handleEsc = (e) => {
            // check if Escape key is pressed and menu is open
            if (e.key === 'Escape' && isOpen) {
                //close the menu
                onClose();
            }
        };
        // Add event listener to the window object
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    // Prevent body scroll when menu is open
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('no-scroll');
        } else {
            document.body.classList.remove('no-scroll');
        }
        return () => document.body.classList.remove('no-scroll');
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40 mobile-overlay-enter"
                onClick={onClose}
            />

            {/* Menu Panel */}
            <div
                ref={mobileMenuRef}
                className="fixed top-0 left-0 h-full bg-white shadow-2xl z-50 overflow-y-auto mobile-menu-scroll mobile-menu-enter"
                style={{
                    width: '320px',
                    maxWidth: '85vw'
                }}
                role="dialog"
                aria-modal="true"
                aria-label="Mobile navigation menu"
            >
                {/* Header */}
                <div className="mobile-menu-header p-4 flex justify-between items-center sticky top-0 z-10">
                    <span className="text-lg font-bold text-white">Menu</span>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors touch-target"
                        aria-label="Close menu"
                    >
                        <FaXmark size={24} className="text-white" />
                    </button>
                </div>

                {/* Menu Items */}
                <div className="flex flex-col py-2">
                    {!menusLoaded ? (
                        <div className="flex justify-center items-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[rgb(27,156,227)]"></div>
                        </div>
                    ) : navbarMenus && navbarMenus.length > 0 ? (
                        navbarMenus.map((menu, i) => (
                            <MobileMenuItem
                                key={i}
                                name={menu.name || ''}
                                link={menu.link || '/'}
                                menus={menu.menus?.length > 0 ? menu.menus : null}
                                setSelected={setSelected}
                                onEnter={onEnter}
                                onLeave={onLeave}
                                closeMobileMenu={onClose}
                                depth={0}
                            />
                        ))
                    ) : (
                        <div className="px-4 py-8 text-center text-gray-500">
                            No menu items available
                        </div>
                    )}
                    
                    {/* Auth Section */}
                    <div className="mobile-auth-container px-4 pb-4">
                        {!user ? (
                            <div className="flex flex-col space-y-2">
                                <button
                                    onClick={() => {
                                        window.location.href = "https://portal.coaz.org/_register";
                                        onClose();
                                    }}
                                    className="w-full py-3 text-center text-white bg-[rgb(27,156,227)] rounded-lg hover:bg-[rgb(20,130,190)] transition-colors touch-target"
                                >
                                    Register
                                </button>
                                <button
                                    onClick={() => {
                                        window.location.href = "https://portal.coaz.org/_login";
                                        onClose();
                                    }}
                                    className="w-full py-3 text-center border-2 border-[rgb(27,156,227)] text-[rgb(27,156,227)] rounded-lg hover:bg-[rgb(27,156,227)] hover:text-white transition-colors touch-target"
                                >
                                    Log In
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col space-y-3 py-2">
                                <EditModeToggle />
                                <UserProfileButton />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default MobileMenu;